// src/pages/admin/AdminUsers.jsx
import * as React from 'react';
import {
  Paper, Typography, Stack, IconButton, CircularProgress, Alert, TextField,
  InputAdornment, List, ListItemButton, ListItemText, Chip, Divider, Avatar, Tooltip,
  Button, Box, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { api } from '../../lib/api';

const BLUE = '#0466c8';
const BLUE_BG = 'rgba(11,95,255,0.06)';
const BORDER = '#e6efe8';
const HOVER_BG = 'rgba(11,95,255,0.03)';
const LANGS = ['fr', 'en', 'pl', 'es', 'it', 'de', 'pt', 'cs', 'nl'];

const roleColor = (r) => {
  switch (r) {
    case 'superadmin': return 'error';
    case 'admin': return 'warning';
    default: return 'default';
  }
};
const roleLabel = (r) => (r === 'vendor' ? 'Client' : (r || '—'));

const fmtDateTime = (v) => {
  if (!v) return null;
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }).format(new Date(v));
  } catch { return null; }
};

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

export default function AdminUsers() {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [snack, setSnack] = React.useState({ open: false, msg: '', severity: 'success' });

  const [dialogUser, setDialogUser] = React.useState(null);
  const [dialogLang, setDialogLang] = React.useState('fr');

  const [perDeleteLoading, setPerDeleteLoading] = React.useState({});

  // --- Edition email ---
  const [editEmailUser, setEditEmailUser] = React.useState(null);
  const [emailInput, setEmailInput] = React.useState('');
  const [emailSaving, setEmailSaving] = React.useState(false);

  // --- Déroulé & édition profil expédition ---
  const [expandedUserId, setExpandedUserId] = React.useState(null);
  const [profileDraftById, setProfileDraftById] = React.useState({});
  const [profileSavingId, setProfileSavingId] = React.useState(null);

  const fetchRows = async () => {
    setErr(''); setLoading(true);
    try {
      const { data } = await api.get('/users');
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => {
        if (a.disabled !== b.disabled) return a.disabled ? 1 : -1; // actifs d'abord
        return new Date(b.lastLoginAt || 0) - new Date(a.lastLoginAt || 0);
      });
      setRows(list);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Erreur de chargement des utilisateurs.');
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => { fetchRows(); }, []);

  const filtered = rows.filter(u => {
    const text = (q || '').trim().toLowerCase();
    if (!text) return true;
    const bag = [
      String(u.email || ''),
      String(u.companyName || ''),
      String(u.name || ''),
      String(u.firstname || ''),
      roleLabel(u.role),
      u.disabled ? 'désactivé' : 'actif',
    ].join(' ').toLowerCase();
    return bag.includes(text);
  });

  const copy = async (txt) => {
    try {
      await navigator.clipboard?.writeText(txt);
      setSnack({ open: true, msg: 'E-mail copié', severity: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Impossible de copier', severity: 'error' });
    }
  };

  const sendReset = async () => {
    if (!dialogUser) return;
    const user = dialogUser;
    setDialogUser(null);
    try {
      await api.post('/auth/forgot', { email: user.email, lang: dialogLang });
      setSnack({ open: true, msg: `Lien envoyé à ${user.email} (${dialogLang.toUpperCase()})`, severity: 'success' });
    } catch (e) {
      const msg = e?.response?.data?.message || 'Échec de l’envoi du lien.';
      setSnack({ open: true, msg, severity: 'error' });
    }
  };

  const removeUser = async (u) => {
    const ok = window.confirm(`Supprimer le compte ${u.email} ? Cette action est définitive.`);
    if (!ok) return;

    setPerDeleteLoading(s => ({ ...s, [u.id]: true }));
    try {
      await api.delete(`/users/${u.id}`);
      setSnack({ open: true, msg: 'Compte supprimé.', severity: 'success' });
      setRows(prev => prev.filter(x => x.id !== u.id));
    } catch (e) {
      const msg = e?.response?.data?.message || 'Suppression impossible.';
      setSnack({ open: true, msg, severity: 'error' });
    } finally {
      setPerDeleteLoading(s => ({ ...s, [u.id]: false }));
    }
  };

  // Toggle disabled
  const toggleDisabled = async (u) => {
    const targetState = !u.disabled;
    const verb = targetState ? 'désactiver' : 'réactiver';
    const ok = window.confirm(`Voulez-vous ${verb} le compte ${u.email} ?`);
    if (!ok) return;

    try {
      const { data } = await api.patch(`/users/${u.id}`, { disabled: targetState, forceLogout: true });
      setRows(prev => prev.map(row => (row.id === u.id ? data : row)));
      setSnack({
        open: true,
        msg: targetState ? 'Compte désactivé et sessions déconnectées.' : 'Compte réactivé.',
        severity: 'success'
      });
    } catch (e) {
      const msg = e?.response?.data?.message || `Impossible de ${verb} le compte.`;
      setSnack({ open: true, msg, severity: 'error' });
    }
  };

  // -------- Edition email UI --------
  const openEditEmail = (u) => { setEditEmailUser(u); setEmailInput(u.email || ''); };
  const closeEditEmail = () => { setEditEmailUser(null); setEmailInput(''); setEmailSaving(false); };

  const saveEmail = async () => {
    if (!editEmailUser) return;
    const target = editEmailUser;
    const value = String(emailInput || '').trim().toLowerCase();

    if (!isEmail(value)) {
      setSnack({ open: true, msg: 'Email invalide', severity: 'error' });
      return;
    }
    if (value === (target.email || '').toLowerCase()) { closeEditEmail(); return; }

    setEmailSaving(true);
    try {
      const { data } = await api.patch(`/users/${target.id}`, { email: value, forceLogout: true });
      setRows(prev => prev.map(u => (u.id === target.id ? data : u)));
      setSnack({ open: true, msg: 'Email mis à jour et sessions déconnectées ✅', severity: 'success' });
      closeEditEmail();
    } catch (e) {
      const msg = e?.response?.data?.message || 'Échec de la mise à jour.';
      setSnack({ open: true, msg, severity: 'error' });
      setEmailSaving(false);
    }
  };

  // -------- Dérouler / éditer profil expédition --------
  const toggleExpandUser = (u) => {
    setExpandedUserId((prev) => (prev === u.id ? null : u.id));
    setProfileDraftById((prev) => {
      if (prev[u.id]) return prev;
      return {
        ...prev,
        [u.id]: {
          companyName: u.companyName || '',
          firstname:   u.firstname   || '',
          name:        u.name        || '',
          phone:       u.phone       || '',
          address:     u.address     || '',
          postalCode:  u.postalCode  || '',
          city:        u.city        || '',
        },
      };
    });
  };

  const updateDraftField = (userId, field, value) => {
    setProfileDraftById((prev) => {
      const current = prev[userId] || {};
      return {
        ...prev,
        [userId]: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const saveProfileForUser = async (u) => {
    const draft = profileDraftById[u.id];
    if (!draft) return;

    setProfileSavingId(u.id);
    try {
      const payload = {
        companyName: draft.companyName || null,
        firstname:   draft.firstname   || null,
        name:        draft.name        || null,
        phone:       draft.phone       || null,
        address:     draft.address     || null,
        postalCode:  draft.postalCode  || null,
        city:        draft.city        || null,
      };

      const { data } = await api.patch(`/users/${u.id}`, payload);
      setRows((prev) => prev.map((row) => (row.id === u.id ? data : row)));

      // resynchroniser le draft avec la réponse du back
      setProfileDraftById((prev) => ({
        ...prev,
        [u.id]: {
          companyName: data.companyName || '',
          firstname:   data.firstname   || '',
          name:        data.name        || '',
          phone:       data.phone       || '',
          address:     data.address     || '',
          postalCode:  data.postalCode  || '',
          city:        data.city        || '',
        },
      }));

      setSnack({ open: true, msg: 'Profil d’expédition mis à jour.', severity: 'success' });
    } catch (e) {
      const msg = e?.response?.data?.message || 'Impossible de mettre à jour le profil.';
      setSnack({ open: true, msg, severity: 'error' });
    } finally {
      setProfileSavingId(null);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        background: '#fff',
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ bgcolor: BLUE, width: 28, height: 28, fontSize: 14, fontWeight: 800 }}>US</Avatar>
          <Typography variant="h6" sx={{ fontWeight: 800, color: BLUE }}>
            Gestion des utilisateurs
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Rechercher (email, nom, rôle…)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: 220, sm: 260 } }}
          />
          <Tooltip title="Rafraîchir la liste">
            <IconButton onClick={fetchRows} disabled={loading} sx={{ color: BLUE }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2, borderColor: BORDER }} />

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography>Chargement…</Typography>
        </Stack>
      ) : filtered.length === 0 ? (
        <Typography sx={{ color: 'text.secondary' }}>
          Aucun utilisateur trouvé.
        </Typography>
      ) : (
        <List dense sx={{ m: 0 }}>
          {filtered.map((u) => {
            const created = fmtDateTime(u.createdAt);
            const lastLogin = fmtDateTime(u.lastLoginAt);
            const fullName = [u.firstname, u.name].filter(Boolean).join(' ') || null;
            const isExpanded = expandedUserId === u.id;
            const draft = profileDraftById[u.id] || {
              companyName: u.companyName || '',
              firstname:   u.firstname   || '',
              name:        u.name        || '',
              phone:       u.phone       || '',
              address:     u.address     || '',
              postalCode:  u.postalCode  || '',
              city:        u.city        || '',
            };

            return (
              <ListItemButton
                key={u.id}
                disableRipple
                sx={{
                  borderRadius: 2,
                  mb: 1.1,
                  p: { xs: 1.1, sm: 1.25 },
                  border: `1px solid ${u.disabled ? '#ffd9d9' : BORDER}`,
                  background: u.disabled ? 'rgba(255,0,0,0.02)' : '#fff',
                  '&:hover': { borderColor: '#cfe1ff', background: HOVER_BG },
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mb: 0.5, cursor: 'pointer' }}
                        onClick={() => toggleExpandUser(u)}
                      >
                        <Chip size="small" label={roleLabel(u.role)} color={roleColor(u.role)} />
                        {u.disabled && <Chip size="small" color="error" label="Désactivé" />}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title="Copier l’adresse e-mail">
                            <Chip
                              icon={<EmailIcon />}
                              size="small"
                              label={u.email}
                              sx={{ bgcolor: BLUE_BG, cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); copy(u.email); }}
                            />
                          </Tooltip>
                          <Tooltip title="Modifier l’e-mail">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); openEditEmail(u); }}
                              sx={{ color: BLUE }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        {u.companyName && <Chip icon={<BusinessIcon />} size="small" label={u.companyName} />}
                        {fullName && <Chip icon={<PersonIcon />} size="small" label={fullName} />}
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title={isExpanded ? 'Masquer le profil' : 'Voir / éditer le profil'}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); toggleExpandUser(u); }}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            icon={<ScheduleIcon />}
                            label={lastLogin ? `Connexion ${lastLogin}` : 'Jamais connecté'}
                          />
                          <Chip
                            size="small"
                            icon={<CalendarMonthIcon />}
                            label={created ? `Créé le ${created}` : '—'}
                          />

                          <Box flexGrow={1} />

                          {/* Désactiver / Réactiver */}
                          <Tooltip title={u.disabled ? 'Réactiver le compte' : 'Désactiver le compte'}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color={u.disabled ? 'success' : 'warning'}
                                startIcon={u.disabled ? <CheckCircleIcon /> : <BlockIcon />}
                                onClick={(e) => { e.stopPropagation(); toggleDisabled(u); }}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  borderColor: BORDER,
                                  '&:hover': { borderColor: BLUE, background: BLUE_BG },
                                }}
                              >
                                {u.disabled ? 'Réactiver' : 'Désactiver'}
                              </Button>
                            </span>
                          </Tooltip>

                          {/* Réinitialiser MDP */}
                          <Tooltip title="Lien de réinitialisation">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<LockResetIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDialogLang('fr');
                                  setDialogUser(u);
                                }}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  borderColor: BORDER,
                                  '&:hover': { borderColor: BLUE, background: BLUE_BG },
                                }}
                              >
                                Réinitialiser
                              </Button>
                            </span>
                          </Tooltip>

                          {/* Supprimer */}
                          <Tooltip title="Supprimer le compte">
                            <span>
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={(e) => { e.stopPropagation(); removeUser(u); }}
                                disabled={!!perDeleteLoading[u.id]}
                                sx={{ minWidth: 'auto', px: 1, py: 0.25 }}
                              >
                                Supprimer
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>
                    }
                  />

                  {/* Bloc déroulant : Profil d'expédition */}
                  {isExpanded && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px dashed ${BORDER}`,
                        bgcolor: '#fafafa',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        Profil d’expédition
                      </Typography>

                      <Stack spacing={1.5}>
                        <TextField
                          label="Entreprise"
                          size="small"
                          fullWidth
                          value={draft.companyName}
                          onChange={(e) => updateDraftField(u.id, 'companyName', e.target.value)}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            label="Prénom"
                            size="small"
                            fullWidth
                            value={draft.firstname}
                            onChange={(e) => updateDraftField(u.id, 'firstname', e.target.value)}
                          />
                          <TextField
                            label="Nom"
                            size="small"
                            fullWidth
                            value={draft.name}
                            onChange={(e) => updateDraftField(u.id, 'name', e.target.value)}
                          />
                        </Stack>

                        <TextField
                          label="Téléphone"
                          size="small"
                          fullWidth
                          value={draft.phone}
                          onChange={(e) => updateDraftField(u.id, 'phone', e.target.value)}
                        />

                        <TextField
                          label="Adresse postale"
                          size="small"
                          fullWidth
                          value={draft.address}
                          onChange={(e) => updateDraftField(u.id, 'address', e.target.value)}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            label="Code postal"
                            size="small"
                            fullWidth
                            value={draft.postalCode}
                            onChange={(e) => updateDraftField(u.id, 'postalCode', e.target.value)}
                          />
                          <TextField
                            label="Ville"
                            size="small"
                            fullWidth
                            value={draft.city}
                            onChange={(e) => updateDraftField(u.id, 'city', e.target.value)}
                          />
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<SaveIcon />}
                            onClick={(e) => { e.stopPropagation(); saveProfileForUser(u); }}
                            disabled={profileSavingId === u.id}
                            sx={{
                              bgcolor: BLUE,
                              '&:hover': { bgcolor: '#004890' },
                            }}
                          >
                            {profileSavingId === u.id ? 'Enregistrement…' : 'Enregistrer le profil'}
                          </Button>
                          <Button
                            size="small"
                            startIcon={<CloseIcon />}
                            onClick={(e) => { e.stopPropagation(); toggleExpandUser(u); }}
                          >
                            Fermer
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </ListItemButton>
            );
          })}
        </List>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        message={snack.msg}
        ContentProps={{
          sx: {
            bgcolor: snack.severity === 'error' ? '#b00020' : '#2e7d32',
          },
        }}
      />

      {/* Dialog réinit MDP */}
      <Dialog
        open={!!dialogUser}
        onClose={() => setDialogUser(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: BLUE,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <LockResetIcon color="primary" /> Réinitialiser le mot de passe
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            Sélectionne la langue du lien envoyé à <b>{dialogUser?.email}</b>
          </Typography>
          <Select
            fullWidth
            size="small"
            value={dialogLang}
            onChange={(e) => setDialogLang(e.target.value)}
          >
            {LANGS.map(code => (
              <MenuItem key={code} value={code}>
                {code.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogUser(null)}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={sendReset}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#004890' } }}
          >
            Envoyer le lien
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog édition email */}
      <Dialog
        open={!!editEmailUser}
        onClose={closeEditEmail}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: BLUE,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <EditIcon color="primary" /> Modifier l’adresse e-mail
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Nouvel e-mail"
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="nouveau@mail.com"
            size="small"
            error={!!emailInput && !isEmail(emailInput)}
            helperText={
              !!emailInput && !isEmail(emailInput) ? 'Format invalide' : ' '
            }
          />
          <Alert severity="warning" icon={<LogoutIcon />} sx={{ mt: 1 }}>
            L’utilisateur sera déconnecté de toutes ses sessions.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={closeEditEmail}>
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveEmail}
            disabled={emailSaving || !isEmail(emailInput)}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#004890' } }}
          >
            {emailSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
